import React, { FC, useMemo, useState } from 'react';
import { Form, Radio, Bubble, Switch, Checkbox } from 'tea-component';
import FormCheckboxGroup from './FormCheckboxGroup';
import AutoFormControlText from './AutoFormControlText';
import MultipleSelector, { renderImgText } from '@/pages/intelligence/components/TeaSelector/MultipleSelector';
import SourceMetricSelector from '@/pages/intelligence/components/TeaSelector/SourceMetricSelector';
import CascadeMetricSelector from '@/pages/intelligence/components/SelectCompoenet/CascadeMetricSelector';
import { CommonFormType, BusinessFormType, BusinessType } from '../types';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import {
  getDataSourcesDisabledStateNew,
  getMetricDisabledNew,
  newMathFormat,
} from '@/pages/intelligence/utils/commonUtils';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import {
  METRICS_MAP,
  DEVICE_Locale_Language,
} from '@/pages/intelligence/utils/constant';
import type { ITranslater } from '@/pages/intelligence/types/common';
import RangePicker from '@/pages/home/components/RangePicker';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { useIntl, getLocale } from 'umi';
import moment from 'moment';
import MultipleCountrySelect from '@/pages/intelligence/components/ComposedCountrySelect/components/MultipleCountrySelect';
import SingleCountrySelect from '@/pages/intelligence/components/ComposedCountrySelect/components/SingleCountrySelect';
import { analysisRegionsCountriesData } from '@/pages/intelligence/components/ComposedCountrySelect/utils';
import { getCountryFlag } from '@/pages/home/_config/countries';
import useDemographicsRegionsOptions from '@/pages/intelligence/hooks/useDemographicsRegionsOptions';
import { addGlobalRegion } from '@/pages/intelligence/v3/utils/region';
import { OPT_MAP } from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { has, get, result, constant } from 'lodash-es';
import type { PCConsolePageId } from '@intelligence/pages/TopCharts/PCGames/_config/type';
import CascaderMetricSelect from '@intelligence/components/CascaderMetricSelect';
import LOCALES_EN from '@intelligence/_locales/en-US/sourceMetrics';
import LOCALES_CN from '@intelligence/_locales/zh-CN/sourceMetrics';
import InfoDocEN from '@intelligence/_locales/en-US/infoDoc';
import InfoDocCN from '@intelligence/_locales/zh-CN/infoDoc';
import { analysisDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import { ampereMetricOptions } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';

export type IType = 'single' | 'multiple' | (string & {});
interface CustomForm {
  formList: CommonFormType[];
  formModel: BusinessFormType;
  onChange: (data: { [key: string]: any }) => void;
  tableParams?: any;
  setFormModelState?: any;
  disabledGsd?: boolean;
  isStudio?: boolean;
  disabledFamitsu?: any;
  disabledNpd?: any;
  disabledMscience?: any;
  locale?: string;
  businessType?: BusinessType;
  regionOptions?: any;
  cascaderDeviceValueList?: any;
  // 用于配合 type为text的控件 展示纯静态文案，但是文案又不能配死在下载表单配置中场景
  texts?: {
    [key: string]: string;
  };
  // REMARK: 补上翻译函数的类型定义
  translater: ITranslater;
  pageId: PCConsolePageId;
  disabledRatio?: any;
  isCalibrated?: boolean;
  dataSource?: string; // 数据源
}

interface IComposedCountryValue {
  type: IType;
  countries: string[];
}

const getMetricsCount = (dataSource: any, childrenOptions?: any[]): number => {
  if (!dataSource) return 0;
  if (dataSource.value === 'npd') return 1;
  return childrenOptions?.length || dataSource?.children?.length || 0;
};

const getDateTypeKey = (dateType?: string): string => {
  const dateTypeMap: Record<string, string> = {
    day: 'day',
    date: 'day',
    daily: 'day',
    week: 'week',
    weekly: 'week',
    month: 'month',
    monthly: 'month',
  };
  return dateTypeMap[dateType || ''] || dateType || '';
};

const normalizeDownloadFilterParams = (
  params: Record<string, any> = {},
): Record<string, any> => ({
  ...params,
  date_type: params.date_type || params.period,
  isCountriesPage: params.isCountriesPage ?? params.isCoutriesPage,
});

const getMetricDisabledBubbleContents = (item?: {
  messagekey?: string;
  message?: string;
  messageCN?: string;
}): { bubbleContent: string; bubbleContentCN: string } => {
  if (!item) {
    return { bubbleContent: '', bubbleContentCN: '' };
  }
  if (item.messagekey) {
    return {
      bubbleContent:
        (InfoDocEN as Record<string, string>)[item.messagekey] || '',
      bubbleContentCN:
        (InfoDocCN as Record<string, string>)[item.messagekey] || '',
    };
  }
  return {
    bubbleContent: item.message || '',
    bubbleContentCN: item.messageCN || item.message || '',
  };
};

const getCascaderChildrenOptions = (
  data: any,
  disabledState: any,
  params: Record<string, any>,
  pageId: PCConsolePageId,
): any[] => {
  if (!data) return [];
  const filterParams = normalizeDownloadFilterParams(params);
  let children;
  if (
    data.value === 'npd' &&
    (!Array.isArray(data.children) || !data.children.length)
  ) {
    children = data.metric ? [data.metric] : [];
  } else {
    children = data.children || [];
  }
  const getMetricValueKey = (value?: string): string =>
    value?.includes('###') ? value.split('###')[1] || '' : value || '';
  // originalStats / Steam 数据 Tab 下载与页面指标筛选器一致，不含分国家愿望单
  if (
    filterParams?.currentMetric === 'steamSales' &&
    !filterParams?.isCountriesPage &&
    data.value === 'alinea'
  ) {
    children = children.filter(
      (item: any) =>
        getMetricValueKey(item?.value) !== 'alinea__wishlists_by_country',
    );
  }
  const dateTypeKey = getDateTypeKey(filterParams?.date_type);
  return children?.map((item: any) => {
    const coutryTabDisable = ['steamfollowers', 'steamreviews'];
    // followers这2个指标在Countries页面时禁用
    const itemDisable =
      filterParams?.isCountriesPage &&
      coutryTabDisable.includes(getMetricValueKey(item?.value));
    // const isMetricDisabled = getMetricDisabled(item?.value, disabledState ? disabledState[data?.value] : false, params);

    const date_type = filterParams?.date_type;
    const isAmpereTarget = ampereMetricOptions.find(
      (opt) => opt.value === item?.value?.split('###')?.[1],
    );
    const isNoMatchDate = !isAmpereTarget?.dateEnum?.includes(date_type);
    const needAmpereItemDisabled = isNoMatchDate && !!isAmpereTarget;
    const isDateTypeMismatch =
      !!item?.ableDateType &&
      !!dateTypeKey &&
      item.ableDateType !== dateTypeKey;

    const isMetricDisabled =
      getMetricDisabledNew({
        pageId,
        metric: item?.value,
        isDataSourceDisabled: disabledState
          ? disabledState[data?.value]
          : false,
        params: filterParams,
      }) ||
      itemDisable ||
      needAmpereItemDisabled ||
      isDateTypeMismatch;

    const disabledBubble = isMetricDisabled
      ? getMetricDisabledBubbleContents({
          messagekey: item?.messagekey,
          message: isDateTypeMismatch
            ? item?.message
            : item?.message || data?.message,
          messageCN: isDateTypeMismatch
            ? item?.messageCN
            : item?.messageCN || data?.messageCN,
        })
      : { bubbleContent: '', bubbleContentCN: '' };

    return {
      ...(item || {}),
      label: (
        <>
          <Bubble
            dark
            className="bubble-all-bg"
            overlayClassName="topcharts-download-bubble"
            trigger="hover"
            placement="top"
            content={
              isMetricDisabled
                ? disabledBubble.bubbleContent || disabledBubble.bubbleContentCN
                : ''
            }
          >
            <div>
              <img
                style={{ marginRight: 4, width: 16, height: 16 }}
                src={getDataSourceIcon(item?.icon || data?.value)}
              />
              <span>{item?.text}</span>
            </div>
          </Bubble>
        </>
      ),
      text: item?.text,
      value: item?.value,
      message: isDateTypeMismatch ? item?.message : item?.message || data?.message,
      messageCN: isDateTypeMismatch
        ? item?.messageCN
        : item?.messageCN || data?.messageCN,
      messagekey: item?.messagekey || data?.messagekey,
      disabled: isMetricDisabled,
      ...disabledBubble,
    };
  });
};

export const getCascaderOptions = (
  data: any[],
  filterParams: any,
  pageId: PCConsolePageId,
): any[] => {
  if (!Array.isArray(data)) return [];
  const { formatMessage } = useIntl();
  const normalizedParams = normalizeDownloadFilterParams(filterParams);

  const disabledState = getDataSourcesDisabledStateNew({
    pageId,
    params: normalizedParams,
  });

  return data.map((item) => {
    const childrenOptions = getCascaderChildrenOptions(
      item,
      disabledState,
      normalizedParams,
      pageId,
    );
    const metricsCount = getMetricsCount(item, childrenOptions);
    const isParentDisabled = disabledState ? disabledState[item?.value] : false;
    const parentDisabledBubble = isParentDisabled
      ? getMetricDisabledBubbleContents(item)
      : { bubbleContent: '', bubbleContentCN: '' };
    return {
      label: (
        <>
          <Bubble
            dark
            className="bubble-all-bg"
            overlayClassName="topcharts-download-bubble"
            trigger="hover"
            placement="top"
            content={
              isParentDisabled
                ? item?.messagekey
                  ? formatMessage({ id: item?.messagekey })
                  : item?.message
                : ''
            }
          >
            <div>
              <img
                style={{ marginRight: 4, width: 16, height: 16 }}
                src={getDataSourceIcon(item?.icon || item?.value)}
              />
              <span>
                {item?.text} {metricsCount > 0 && `(${metricsCount})`}
              </span>
            </div>
          </Bubble>
        </>
      ),
      text: item?.text,
      value: item?.value,
      message: item?.message,
      messageCN: item?.messageCN,
      messagekey: item?.messagekey,
      disabled: isParentDisabled,
      children: childrenOptions,
      icon: item?.icon,
      ...parentDisabledBubble,
    };
  });
};

const getCascaderOptionsPre = (
  cascadeOptionsList?: any[],
  payload?: {
    tableParams?: Record<string, any>;
    ifUseMetricsMap?: boolean;
  },
) => {
  const ifUseMetricsMap = payload?.ifUseMetricsMap || false;
  const tableParams = payload?.tableParams || {};
  let cascaderOptions: any[];
  if (ifUseMetricsMap) {
    const key = `${tableParams?.entity_type?.toLowerCase()}-detail`;
    cascaderOptions = METRICS_MAP[key];
  } else {
    cascaderOptions = cascadeOptionsList || [];
  }
  return Array.isArray(cascaderOptions) ? cascaderOptions : [];
};

export const getCascadeOptionsNew = (
  cascadeOptionsList?: any[],
  payload?: {
    pageId?: any;
    tableParams?: Record<string, any>;
    ifUseMetricsMap?: boolean;
  },
) => {
  const tableParams = normalizeDownloadFilterParams(payload?.tableParams || {});
  const pageId = payload?.pageId || '';
  const cascaderOptionsPre = getCascaderOptionsPre(cascadeOptionsList, {
    ...payload,
    tableParams,
  });
  const cascaderOptions = getCascaderOptions(
    cascaderOptionsPre,
    tableParams,
    pageId,
  );
  return Array.isArray(cascaderOptions) ? cascaderOptions : [];
};

export const getCascaderDeviceValue = (
  valueData: any[],
  filterParams: any,
  pageId: PCConsolePageId,
  field: string,
): any[] => {
  // console.log('valueDatavalueData', valueData)
  if (!Array.isArray(valueData)) return [];
  const valueList: any = [];
  const disabledState = getDataSourcesDisabledStateNew({
    pageId,
    params: filterParams,
  });
  // console.log('disabledStatedisabledState', disabledState)
  valueData.forEach((item) => {
    const valueKey = analysisDataSourceMetricKey(item);
    if (!disabledState || !disabledState[valueKey?.dataSource]) {
      valueList.push(item);
    }
  });
  // console.log('valueListvalueList,', valueList);
  // console.log('cascaderDeviceValueListcascaderDeviceValueList,', cascaderDeviceValueList);
  // onChange({ [field]: valueList });
  // setFormModelState({
  //   ...formModel,
  //   downloadDevices: valueList
  // });
  // cascaderDeviceValueList?.current = valueList;
  return valueList;
};

const updateChildrenDisabledState = (
  nodes?: any[],
  disabled?: boolean,
): any[] => {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node) => {
    return { ...node, disabled: !!disabled };
  });
};
const getCascaderDeviceOptionsNew = (
  data: any[],
  filterParams: any,
  pageId: PCConsolePageId,
): any[] => {
  if (!Array.isArray(data)) return [];
  const disabledState = getDataSourcesDisabledStateNew({
    pageId,
    params: filterParams,
  });
  return data.map((item) => {
    const itemDisabled = disabledState[item?.value] || false;
    return {
      ...item,
      disabled: itemDisabled,
      children: updateChildrenDisabledState(item?.children, itemDisabled),
      bubbleContent: itemDisabled ? item?.bubbleContent : '',
      bubbleContentCN: itemDisabled ? item?.bubbleContentCN : '',
    };
  });
};
const getMetricOptions = (
  options: any[],
  filterParams: any,
  pageId: PCConsolePageId,
) => {
  return options;
};
const getCombineDeatailCascadeOptions = (cascadeOptions: any[]) => {
  return cascadeOptions?.map((item: any) => {
    const newItem = { ...item };

    // cascadeOptions 来自 getDeatilDowloadMetricCascadeOption，
    // 其中 icon 已经通过 getDataSourceIcon 转换为实际图片路径，
    // 这里不能再次调用 getDataSourceIcon，否则会导致二次转换得到空值。
    const parentIcon = newItem?.icon || null;
    const parentDisabledBubble = newItem?.disabled
      ? getMetricDisabledBubbleContents(newItem)
      : { bubbleContent: '', bubbleContentCN: '' };

    if (newItem?.children && Array.isArray(newItem.children)) {
      newItem.children = newItem.children.map((child: any) => {
        const childDisabledBubble = child?.disabled
          ? newItem?.disabled
            ? parentDisabledBubble
            : child?.bubbleContent || child?.bubbleContentCN
            ? {
                bubbleContent: child.bubbleContent || '',
                bubbleContentCN: child.bubbleContentCN || '',
              }
            : getMetricDisabledBubbleContents({
                messagekey: child?.messagekey,
                message: child?.message,
                messageCN: child?.messageCN,
              })
          : { bubbleContent: '', bubbleContentCN: '' };
        const newChild: any = {
          ...child,
          ...childDisabledBubble,
        };
        // 二级指标：icon 已在 getDeatilDowloadMetricCascadeOption 中转换完毕，
        // 如果子级没有 icon，则回退使用父级的 icon
        if (child?.icon && child.icon !== '') {
          newChild.icon = child.icon;
        } else if (parentIcon) {
          newChild.icon = parentIcon;
        }
        return newChild;
      });
    }

    const result: any = {
      ...newItem,
      ...parentDisabledBubble,
    };

    // 一级指标保留已转换的图标
    if (parentIcon) {
      result.icon = parentIcon;
    }

    return result;
  });
};
const CustomForm: FC<CustomForm> = (props) => {
  const {
    formList,
    formModel,
    onChange,
    tableParams,
    setFormModelState,
    businessType,
    disabledGsd,
    disabledFamitsu,
    disabledNpd,
    disabledMscience,
    texts = {},
    translater,
    regionOptions,
    pageId,
    disabledRatio,
    isStudio,
    // cascaderDeviceValueList,
    isCalibrated,
  } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  // console.log('CustomForm', props);

  const { groups, options: countryOptions } = analysisRegionsCountriesData(
    regionOptions,
    locale,
  );
  const [searchKeywords, setSearchKeywords] = useState('');

  // ===== 下载弹窗单选国家：切换 lifetime/quarter 时动态获取国家选项 =====
  const downloadTimeType = formModel?._downloadTimeType;
  const dynamicGranularity = useMemo(() => {
    if (downloadTimeType === 'quarter') return 'quarterly';
    return 'lifetime';
  }, [downloadTimeType]);

  const {
    regionOptions: dynamicRegionOptions,
    marketLoading: dynamicMarketLoading,
  } = useDemographicsRegionsOptions({
    appIds: [],
    source: tableParams?.source || 'sensortower',
    granularity: dynamicGranularity,
    waitForParams: true,
  });

  // 和页面一致：SensorTower 时在头部插入 Global
  const dynamicRegionOptionsWithGlobal = useMemo(
    () => addGlobalRegion(dynamicRegionOptions),
    [dynamicRegionOptions],
  );

  const {
    groups: dynamicGroups,
    options: dynamicCountryOptions,
  } = useMemo(
    () => analysisRegionsCountriesData(dynamicRegionOptionsWithGlobal as any, locale),
    [dynamicRegionOptionsWithGlobal, locale],
  );

  // 加国旗 icon（和页面筛选器 ComposedCountrySelect 中 optionsPlus 一致）
  const dynamicCountryOptionsWithFlag = useMemo(() => {
    if (!Array.isArray(dynamicCountryOptions)) return [];
    return dynamicCountryOptions.map((option: any) => {
      const countryCode = option?.value ?? '';
      const countryName = option?.text ?? '';
      return {
        ...option,
        name: countryName,
        text: (
          <div className="dbi-filter-selector-option-node">
            <img
              className="dbi-filter-selector-option-node-icon"
              src={getCountryFlag(countryCode)}
              alt=""
            />
            <span className="dbi-filter-selector-option-node-text">{countryName}</span>
          </div>
        ),
      };
    });
  }, [dynamicCountryOptions]);
  // ===== 下载弹窗单选国家 END =====

  const renderOptionName = (name: string, code: string) => {
    // if (!highlightSearchKeywords) return name;
    if (!searchKeywords?.trim()?.length) return name;
    // 单选、并且 当前option就是选中项时，不高亮
    // if (value?.countries?.[0] === code) return name;
    const searchKeywordsChars = searchKeywords
      ?.split('')
      ?.filter((item) => item);
    const reg = new RegExp(searchKeywordsChars?.join('|'), 'ig');
    return name?.replace(
      reg,
      (item) => `<span class="dbi-country-highlight">${item}</span>`,
    );
  };

  const getCascaderDeviceOptions = (
    data: any[],
    filterParams: any,
    pageId: PCConsolePageId,
  ): any[] => {
    if (!Array.isArray(data)) return [];
    const disabledState = getDataSourcesDisabledStateNew({
      pageId,
      params: filterParams,
    });
    return data.map((item) => {
      if (disabledState && disabledState[item?.value]) {
        return {
          ...item,
          disabled: true,
        };
      }
      return {
        ...item,
        bubbleContent: '',
        bubbleContentCN: '',
      };
    });
  };

  const getCascadeOptionsTemp = (cascadeOptionsList: any[]) => {
    const { tableParams = {} } = props;
    const {
      entity_type = '',
      metricList = [],
      metrics = [],
      disabledMetricList = [],
    } = tableParams;
    if (entity_type === 'pc' || entity_type === 'console' || entity_type === 'mobile') {
      const isMobile = entity_type === 'mobile';
      const returnList = metrics
        ?.map((itemM: any, index: any) => {
          const metricsObj =
            metricList?.find((metric: any) =>
              isMobile ? metric.key === itemM : metric.value == itemM,
            ) || {};
          if (Object.keys(metricsObj).length === 0) {
            return {};
          }
          const isRetention =
            isMobile &&
            (itemM === 'appannie_retention' || itemM === 'sensortower_retention');
          return {
            id: index * 1 + 1,
            img:
              metricsObj?.icon ||
              metricsObj?.dataSource ||
              metricsObj?.apiSource ||
              '',
            title: isRetention
              ? formatMessage({ id: 'dbi_retention' })
              : metricsObj?.text || '',
            value: isMobile ? itemM : metricsObj?.value || '',
          };
        })
        ?.filter((itemSub: any) => Object.keys(itemSub).length !== 0);

      return returnList?.filter(
        (itemSub: any) =>
          !disabledMetricList?.some((tt: any) => tt.value == itemSub.value),
      );
    }

    return cascadeOptionsList;
  };

  const getCascaderCompanyChildrenOptions = (data: any): any[] => {
    if (!data) return [];
    let children;
    children = data.children || [];
    return children?.map((item: any) => {
      return {
        label: (
          <div>
            <img
              style={{ marginRight: 4, width: 16, height: 16 }}
              src={getDataSourceIcon(
                item?.icon || item?.tableSource || data?.value,
              )}
            />
            <span>{item?.text}</span>
          </div>
        ),
        text: item?.text,
        value: item?.value,
        icon: item.icon,
      };
    });
  };

  const getCascaderCompanyOptions = (data: any[]): any[] => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      const metricsCount = getMetricsCount(item);
      return {
        label: (
          <div>
            <span>
              {item?.text} {metricsCount > 0 && `(${metricsCount})`}
            </span>
          </div>
        ),
        text: item?.text,
        value: item?.value,
        isNoIcon: item?.isNoIcon || false,
        children: getCascaderCompanyChildrenOptions(item),
      };
    });
  };

  const getOptionsCur = (item: any) => {
    //  { label: 'data.ai', value: 'appannie' },
    // console.log('itemitem546654534435', item);
    if (
      businessType === 'mobile-rpd' &&
      tableParams?.data_source?.length > 0 &&
      item.field === 'data_sources' &&
      tableParams?.data_source?.[0] === 'appannie'
    ) {
      return [{ label: 'data.ai', value: 'appannie' }];
    }
    if (isCalibrated && item.field === 'data_sources') {
      return [...item.options, { label: 'DataBrain', value: 'ogdb' }];
    }
    return item.options;
  };

  const renderCustomForm = (item: CommonFormType) => {
    const {
      type,
      options = [],
      field = '',
      className,
      cascadeOptions = [],
      // isNeedMoreMarginTop,
    } = item || {};

    const childrenFormList = item?.children || [];

    const cascadeOptionsTemp = getCascadeOptionsTemp(cascadeOptions);

    // console.debug('in renderCustomForm', { type, cascadeOptionsTemp, cascadeOptions });

    // 后期可以将不同类型组件抽离
    switch (type) {
      case 'text': {
        const text = item?.text ? texts?.[item?.text] : '';
        return <Form.Text>{translater?.({ id: text })}</Form.Text>;
      }
      case 'checkboxGroup':
        return (
          <FormCheckboxGroup
            options={getOptionsCur(item)}
            formModel={formModel}
            field={field}
            onChange={onChange}
            // disabledCheckbox={disabledCheckbox}
            disabledGsd={disabledGsd}
            disabledFamitsu={disabledFamitsu}
            disabledNpd={disabledNpd}
            disabledMscience={disabledMscience}
            setFormModelState={setFormModelState}
            tableParams={tableParams}
            translater={translater}
            showBatchOperation={item?.showBatchOperation}
          />
        );
      case 'multipleSelect': {
        const multipleSelectOptions = getOptionsCur(item)?.map((option: any) => {
          return {
            ...option,
            text: translater?.({ id: option?.label || option?.text }),
          };
        });

        return (
          <MultipleSelector
            className={className}
            hasAllCheck
            options={multipleSelectOptions}
            value={formModel?.[field] || []}
            onChange={(value: string[]) => {
              onChange?.({ [field]: value });
            }}
          />
        );
      }
      case 'checkbox':
        return (
          <div className="dbi-checkbox-grid">
            {options?.map((item, index) => (
              <Checkbox
                key={index}
                value={formModel?.[item?.value]}
                onChange={(value) => onChange({ [item.value]: value })}
              >
                {translater?.({ id: item?.label })}
              </Checkbox>
            ))}
          </div>
        );
      case 'radio': {
        return (
          <AutoFormControlText
            options={item?.options}
            translater={translater}
            formControlRenderer={() => (
              <Radio.Group
                className="dbi-radio-group"
                value={formModel?.[field]}
                onChange={(value) => {
                  onChange({ [field]: value });
                }}
              >
                {options?.length > 0 &&
                  options?.map((config) => (
                    <Radio name={config?.value} key={config.value}>
                      {translater?.({ id: config?.label })}
                    </Radio>
                  ))}
                {item?.message && (
                  <TipContext
                    className="dbi-tip-icon"
                    AWidth={381}
                    tipText={translater?.({ id: item?.message })}
                  />
                )}
              </Radio.Group>
            )}
          />
        );
      }

      case 'switch':
        return businessType === 'overlap-mobile-games' &&
          field === 'ratio_show' ? (
            <Switch
              value={formModel?.[field]}
              disabled={disabledRatio}
              onChange={(value) => {
                onChange({ [field]: value });
              }}
            />
          ) : (
            <Switch
              value={formModel?.[field]}
              onChange={(value) => {
                onChange({ [field]: value });
              }}
            />
          );

      case 'sourceMetricSelector': {
        return (
          <SourceMetricSelector
            className={className}
            options={options}
            value={formModel?.[field]}
            onConfirm={(value) => {
              onChange({ [field]: value });
            }}
          />
        );
      }

      case 'sourceMetricSelectorNew': {
        return (
          <CascaderMetricSelect
            locale={locale}
            className="dbi-downloads-device"
            options={item?.options || []}
            value={formModel?.[field]}
            onConfirm={(value: string[]) => {
              onChange({ [field]: value });
            }}
          />
        );
      }
      case 'sourceMetricSelectorDetail': {
        const { cascadeOptions = [] } = item;
        // console.debug('cascadeOptions in detail', { cascadeOptions });

        return (
          <CascaderMetricSelect
            locale={locale}
            className="dbi-downloads-device"
            options={getCombineDeatailCascadeOptions(cascadeOptions)}
            buttonType="4"
            language={{
              en: {
                region_title: LOCALES_EN['dbi-sm-filter-datasource'],
              },
              cn: {
                region_title: LOCALES_CN['dbi-sm-filter-datasource'],
              },
            }}
            value={formModel?.[field]}
            onConfirm={(value: string[]) => {
              onChange({ [field]: value });
            }}
          />
        );
      }
      case 'minigameMetricSelector': {
        const { options: optionsOuter, field = '' } = item;
        const selectorOptions = (optionsOuter || []).map((opt: any) => ({
          text: renderImgText(translater?.({ id: opt?.label || opt?.text }) as string, opt?.icon),
          value: opt?.value,
        }));
        return (
          <MultipleSelector
            hasAllCheck={false}
            options={selectorOptions}
            value={formModel?.[field] || []}
            onChange={(value: string[]) => {
              onChange?.({ [field]: value });
            }}
          />
        );
      }
      case 'downloadsDevice': {
        const deviceCascaderOptions = item?.options || [];
        return (
          <CascaderMetricSelect
            className="dbi-downloads-device"
            locale={locale}
            language={{
              en: {
                region_title: LOCALES_EN['dbi-sm-filter-datasource'],
              },
              cn: {
                region_title: LOCALES_CN['dbi-sm-filter-datasource'],
              },
            }}
            buttonType="2"
            options={getCascaderDeviceOptionsNew(
              deviceCascaderOptions,
              tableParams,
              pageId,
            )}
            value={getCascaderDeviceValue(
              formModel?.[field],
              tableParams,
              pageId,
              field,
            )}
            onConfirm={(value: string[]) => {
              onChange({ [field]: value });
            }}
          />
        );
      }

      case 'cascadeMetricSelector': {
        return (
          <CascadeMetricSelector
            value={formModel?.[field]}
            options={cascadeOptions}
            onChange={(value: any) => {
              onChange({ [field]: value });
            }}
          />
        );
      }

      case 'cascadeCompanyMetricSelector': {
        return (
          <CascadeMetricSelector
            value={formModel?.[field]}
            options={getCascaderCompanyOptions(cascadeOptions)}
            onChange={(value: any) => {
              onChange({ [field]: value });
            }}
            isCompany={true}
          />
        );
      }

      case 'followingMetric': {
        return (
          <div className="download-r">
            <div className="download-r-text">
              {cascadeOptionsTemp?.map((item, i) => (
                <div className="download-r-text-item" key={item.id}>
                  <img
                    src={getDataSourceIcon(item.img)}
                    alt=""
                    className="download-r-text-img"
                  />
                  {item.title}
                  {cascadeOptionsTemp.length == i + 1 ? '' : ','}
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'dateRangeMonth': {
        const params = props?.tableParams;
        const dateValue = [
          params?.start_date || params?.date,
          params?.end_date || params?.date,
        ];
        return (
          <div className="search-item-date">
            <RangePicker
              type="month"
              className="search-border"
              overlayClassName="dbi-high-layer"
              typeSelectOverlayClassName="dbi-high-layer"
              value={
                formModel?.[field] && formModel?.[field]?.length > 1
                  ? [
                    moment(formModel?.[field][0]),
                    moment(formModel?.[field][1]),
                  ]
                  : ['', '']
              }
              showSelectType={false}
              language={locale === LANGUAGE_CN ? 'cn' : 'en'}
              onChange={(value: any, type: any) => {
                let dateValue = ['', ''];
                // 月粒度时，enddate为当前月的最后一天而不是第一天
                if (Array.isArray(value) && value.length === 2) {
                  const startDate = value[0];
                  let endDate = value[1];
                  /** 切换左侧日周月时，因为默认值为空，所以这里默认值给切换的值 */
                  if (
                    businessType === 'overlap-mobile-games' &&
                    type === 'month'
                  ) {
                    // 月粒度为 1-31号的格式
                    endDate = moment(endDate).endOf('month');
                  }
                  dateValue = [
                    moment(startDate).format('YYYY-MM-DD'),
                    moment(endDate).format('YYYY-MM-DD'),
                  ];
                }
                onChange({ [field]: dateValue });
              }}
            />
            {item?.message && (
              <TipContext
                className="dbi-tip-icon"
                AWidth={381}
                tipText={translater?.({ id: item?.message })}
              />
            )}
          </div>
        );
      }
      case 'dateRangeDay': {
        const params = props?.tableParams;
        const dateValue = [
          params?.start_date || params?.date,
          params?.end_date || params?.date,
        ];
        return (
          <div className="search-item-date">
            <RangePicker
              type="date"
              className="search-border"
              overlayClassName="dbi-high-layer"
              typeSelectOverlayClassName="dbi-high-layer"
              value={
                formModel?.[field] && formModel?.[field]?.length > 1
                  ? [
                    moment(formModel?.[field][0]),
                    moment(formModel?.[field][1]),
                  ]
                  : ['', '']
              }
              showSelectType={false}
              language={locale === LANGUAGE_CN ? 'cn' : 'en'}
              onChange={(value: any) => {
                let dateValue = ['', ''];
                // 月粒度时，enddate为当前月的最后一天而不是第一天
                if (Array.isArray(value) && value.length === 2) {
                  const startDate = value[0];
                  const endDate = value[1];
                  dateValue = [
                    moment(startDate).format('YYYY-MM-DD'),
                    moment(endDate).format('YYYY-MM-DD'),
                  ];
                }
                onChange({ [field]: dateValue });
              }}
            />
            {item?.message && (
              <TipContext
                className="dbi-tip-icon"
                AWidth={381}
                tipText={translater?.({ id: item?.message })}
              />
            )}
          </div>
        );
      }

      case 'dateRangeDynamic': {
        const dynamicTypeList = item?.typeList || ['month'];
        const dynamicDefaultType = item?.defaultType || dynamicTypeList[0];
        const dynamicInputDisabled = item?.inputDisabled || false;
        const dynamicCustomOption = item?.customOption || {};
        const dynamicOptionsMap = item?.optionsMap;

        // lifetime 的日期取页面记录的初始 lifetime 时间（刚进入页面时后端返回的），不会随 quarter 切换而改变
        const lifetimeDateRef = React.useRef<string[]>([
          tableParams?._lifetimeStartDate || tableParams?.start_date || '',
          tableParams?._lifetimeEndDate || tableParams?.end_date || '',
        ]);
        // quarter 最新日期，用于从 lifetime 切到 quarter 时回填
        const quarterLatestDateRef = React.useRef<string>(tableParams?._quarterLatestDate || '');
        const [currentTimeType, setCurrentTimeType] = React.useState(dynamicDefaultType);

        return (
          <div className="search-item-date">
            <RangePicker
              type={currentTimeType}
              typeList={dynamicTypeList}
              showSelectType={dynamicTypeList.length > 1}
              inputDisabled={dynamicInputDisabled}
              customOption={dynamicCustomOption}
              optionsMap={dynamicOptionsMap}
              selectClassName={`search-border${item?.selectClassName ? ` ${item.selectClassName}` : ''}`}
              customComponentClassName={`search-border${item?.customComponentClassName ? ` ${item.customComponentClassName}` : ''}`}
              rangePickerClassName={`search-border${item?.rangePickerClassName ? ` ${item.rangePickerClassName}` : ''}`}
              overlayClassName={`dbi-high-layer${item?.overlayClassName ? ` ${item.overlayClassName}` : ''}`}
              typeSelectOverlayClassName="dbi-high-layer"
              value={
                formModel?.[field] && formModel?.[field]?.length > 1 && formModel?.[field][0] && formModel?.[field][1]
                  ? [moment(formModel?.[field][0]), moment(formModel?.[field][1])]
                  : undefined
              }
              language={locale === LANGUAGE_CN ? 'cn' : 'en'}
              onChange={(value: any, type: any) => {
                let dateValue = ['', ''];
                if (type && type !== currentTimeType) {
                  // 用户切换了时间类型
                  setCurrentTimeType(type);
                  // 切回 lifetime 时恢复后端返回的固定日期
                  if (type === 'lifetime' && lifetimeDateRef.current?.[0] && lifetimeDateRef.current?.[1]) {
                    onChange({
                      [field]: lifetimeDateRef.current,
                      _downloadTimeType: type,
                    });
                    return;
                  }
                  // 切到 quarter 时：优先用缓存的 quarter 最新日期回填，否则清空让用户重选
                  if (type === 'quarter' && quarterLatestDateRef.current) {
                    onChange({
                      [field]: [quarterLatestDateRef.current, quarterLatestDateRef.current],
                      _downloadTimeType: type,
                    });
                    return;
                  }
                  onChange({
                    [field]: ['', ''],
                    _downloadTimeType: type,
                  });
                  return;
                }
                if (Array.isArray(value) && value.length === 2) {
                  const startDate = value[0];
                  const endDate = value[1];
                  if (type === 'quarter' || currentTimeType === 'quarter') {
                    const useQuarterStart = dynamicCustomOption?.quarter?.useStartOfQuarter;
                    dateValue = [
                      moment(startDate).startOf('quarter').format('YYYY-MM-DD'),
                      useQuarterStart
                        ? moment(endDate).startOf('quarter').format('YYYY-MM-DD')
                        : moment(endDate).endOf('quarter').format('YYYY-MM-DD'),
                    ];
                  } else {
                    dateValue = [
                      moment(startDate).format('YYYY-MM-DD'),
                      moment(endDate).format('YYYY-MM-DD'),
                    ];
                  }
                }
                onChange({ [field]: dateValue, _downloadTimeType: type || currentTimeType });
              }}
            />
            {item?.message && (
              <TipContext
                className="dbi-tip-icon"
                AWidth={381}
                tipText={translater?.({ id: item?.message })}
              />
            )}
          </div>
        );
      }

      case 'countrySettings': {
        const params = props?.tableParams;
        return (
          <div className={'search-item dbi-composed-country-select'}>
            <MultipleCountrySelect
              isAll={false}
              style={{ width: 282 }}
              className="dbi-composed-country-select_country-select dbi-composed-country-select_multiple-country-select"
              groups={groups}
              renderOptionName={renderOptionName}
              options={countryOptions}
              value={formModel?.[field] || params?.markets}
              // value={formModel?.[field]}
              onConfirm={(payload: string[]) => {
                onChange({ [field]: payload });
              }}
              onSearch={(payload?: string) => {
                const keywordsTmp = payload ?? '';
                setSearchKeywords(keywordsTmp);
              }}
              onClose={() => {
                setSearchKeywords('');
              }}
            />
            {item?.message && (
              <TipContext
                className="dbi-tip-icon"
                AWidth={381}
                tipText={translater?.({ id: item?.message })}
              />
            )}
          </div>
        );
      }

      case 'countrySingleSelect': {
        const params = props?.tableParams;
        const currentValue = formModel?.[field] || params?.markets || ['global'];
        return (
          <div className={'search-item dbi-composed-country-select'}>
            <MultipleCountrySelect
              isAll={false}
              style={{ width: 282 }}
              className="dbi-composed-country-select_country-select dbi-composed-country-select_multiple-country-select"
              overlayClassName="dbi-high-layer dbi-composed-country-select-overlay"
              disabled={dynamicMarketLoading}
              groups={dynamicGroups}
              renderOptionName={renderOptionName}
              options={dynamicCountryOptionsWithFlag}
              value={currentValue}
              onConfirm={(payload: string[]) => {
                onChange({ [field]: payload });
              }}
              onSearch={(payload?: string) => {
                const keywordsTmp = payload ?? '';
                setSearchKeywords(keywordsTmp);
              }}
              onClose={() => {
                setSearchKeywords('');
              }}
            />
            {item?.message && (
              <TipContext
                className="dbi-tip-icon"
                AWidth={381}
                tipText={translater?.({ id: item?.message })}
              />
            )}
          </div>
        );
      }

      case 'revenueSettings': {
        const { revenue_setting = {} } = formModel;
        const title = useMemo(() => {
          const dataSource = Object.keys(revenue_setting);
          if (!dataSource?.length) {
            return (
              <>
                <span>
                  {`${formatMessage({
                    id: 'dbi_revenue_range',
                  })} ${formatMessage({
                    id: 'dbi_all',
                  })}`}{' '}
                </span>
                <span className="dbi-revenue-settings-divide">/</span>
                <span>
                  {`${formatMessage({
                    id: 'dbi_revenue_growth',
                  })} ${formatMessage({
                    id: 'dbi_all',
                  })}`}{' '}
                </span>
              </>
            );
          }
          const configList = revenue_setting?.[dataSource[0]] || [];
          const growth = configList?.find((item) => item?.ratio_type === 1);
          const minRange = configList?.find(
            (item) => item?.opt === '>=' && !has(item, 'ratio_type'),
          );
          const maxRange = configList?.find(
            (item) => item?.opt === '<' && !has(item, 'ratio_type'),
          );

          const renderRange = (minRange, maxRage) => {
            if (minRange?.value && maxRage?.value) {
              return `${formatMessage({
                id: 'dbi_revenue_range',
              })} ${newMathFormat(minRange?.value, true)} ~ ${newMathFormat(
                maxRage?.value,
                true,
              )}`;
            }

            if (minRange?.value && !maxRage?.value) {
              return `${formatMessage({
                id: 'dbi_revenue_range',
              })} ${newMathFormat(minRange?.value, true)} ~ -`;
            }

            if (!minRange?.value && maxRage?.value) {
              return `${formatMessage({
                id: 'dbi_revenue_range',
              })} - ~ ${newMathFormat(maxRage?.value, true)}`;
            }
            return `${formatMessage({
              id: 'dbi_revenue_range',
            })} ${formatMessage({
              id: 'dbi_all',
            })}`;
          };
          const growthTitle = `${OPT_MAP?.[growth?.opt] || growth?.opt}  ${
            growth?.value
          }%`;
          return (
            <>
              <span>{renderRange(minRange, maxRange)}</span>
              <span className="dbi-revenue-settings-divide">/</span>
              <span>
                {' '}
                {formatMessage({ id: 'dbi_revenue_growth' })}{' '}
                {!growth ? formatMessage({ id: 'dbi_all' }) : growthTitle}
              </span>
            </>
          );
        }, [revenue_setting]);
        return <div className="dbi-revenue-settings">{title}</div>;
      }

      case 'metricSettings': {
        if (isStudio) {
          return null;
        }
        const metricFilter = get(tableParams, 'metric_filter', {});
        const source = get(options, '0.label', '');
        const metric = get(options, '0.value', '');
        const list = get(metricFilter, source, []);
        const rangeList = list?.filter((item) => item.metric === metric) || [];
        if (!rangeList?.length)
          return (
            <div className="dbi-revenue-settings">
              {formatMessage({ id: 'dbi_all' })}
            </div>
          );
        const minRange = rangeList?.find(
          (item) => item?.opt === '>=' && !has(item, 'ratio_type'),
        );
        const maxRange = rangeList?.find(
          (item) => item?.opt === '<' && !has(item, 'ratio_type'),
        );

        if (minRange?.value && maxRange?.value) {
          return (
            <div className="dbi-revenue-settings">{`${newMathFormat(
              minRange?.value,
              true,
            )} ~ ${newMathFormat(maxRange?.value, true)}`}</div>
          );
        }

        if (minRange?.value && !maxRange?.value) {
          return (
            <div className="dbi-revenue-settings">{`${newMathFormat(
              minRange?.value,
              true,
            )} ~ -`}</div>
          );
        }

        if (!minRange?.value && maxRange?.value) {
          return (
            <div className="dbi-revenue-settings">{`~ ${newMathFormat(
              maxRange?.value,
              true,
            )}`}</div>
          );
        }
      }

      case 'revenueUnitSettings': {
        const revenueRangeUnit = get(tableParams, 'revenue_range_unit', {});
        const title = useMemo(() => {
          const minValue = get(revenueRangeUnit, 'min.value', '');
          const maxValue = get(revenueRangeUnit, 'max.value', '');
          const minUnit = get(revenueRangeUnit, 'max.unit', '');
          const maxUnit = get(revenueRangeUnit, 'max.unit', '');
          if (minValue && maxValue) {
            return `${minValue}${
              minValue !== '0' ? minUnit : ''
            } ~ ${maxValue}${maxUnit !== '0' ? maxUnit : ''}`;
          }

          if (minValue && !maxValue) {
            return `${minValue}${minValue !== '0' ? minUnit : ''} ~`;
          }
          if (!minValue && maxValue) {
            return `~ ${maxValue}${maxUnit !== '0' ? maxUnit : ''}`;
          }
          return formatMessage({ id: 'dbi_all' });
        }, [revenueRangeUnit, locale]);

        return <div className="dbi-revenue-settings">{title}</div>;
      }

      // 子表单 Data Calculation
      case 'subForm':
        return childrenFormList?.length > 0 ? (
          <Form className="dbi-form-item--subForm">
            {childrenFormList?.map((config: any) => {
              if (
                config.field === 'download_date_type' &&
                formModel?.downlaod_date_method !== 'separate'
              ) {
                return null;
              }
              if (
                businessType === 'genre-profile' &&
                config?.field === 'genre_source' &&
                formModel?.game_platform !== 'mobile'
              ) {
                return null;
              }
              if (isStudio && config.type == 'metricSettings') {
                return null;
              }
              return (
                <Form.Item
                  label={translater?.({ id: config?.label })}
                  showStatusIcon={false}
                  className={`${
                    config.type == 'sourceMetricSelector' ||
                    config.type == 'countrySettings' ||
                    config.type == 'countrySingleSelect' ||
                    config.type == 'dateRangeMonth' ||
                    config.type == 'dateRangeDay' ||
                    config.type == 'dateRangeDynamic' ||
                    config.type == 'cascadeCompanyMetricSelector' ||
                    config.type == 'cascadeMetricSelector' ||
                    config.type == 'sourceMetricSelector' ||
                    config.type == 'downloadsDevice'
                      ? 'form-item--top-8'
                      : ''
                  }`}
                >
                  {renderCustomForm(config)}
                </Form.Item>
              );
            })}
          </Form>
        ) : null;

      default:
        return '';
    }
  };
  return (
    <>
      {formList?.map((item: any) => {
        if (
          businessType === 'genre-profile-detail' &&
          item?.field === 'game_platform' &&
          formModel?.genre_source === 'appannie'
        ) {
          return null;
        }
        return (
          <Form.Item
            label={translater?.({
              id:
                isStudio && item?.label == 'dbi_device'
                  ? 'dbi_platform'
                  : item?.label,
            })}
            showStatusIcon={false}
            className={`${
              item.type == 'subForm'
                ? 'form-item--top-18'
                : item.type == 'sourceMetricSelector' ||
                  item.type == 'countrySettings' ||
                  item.type == 'countrySingleSelect' ||
                  item.type == 'dateRangeMonth' ||
                  item.type == 'dateRangeDay' ||
                  item.type == 'dateRangeDynamic' ||
                  item.type == 'cascadeCompanyMetricSelector' ||
                  item.type == 'cascadeMetricSelector' ||
                  item.type == 'sourceMetricSelector' ||
                  item.type == 'downloadsDevice'
                ? 'form-item--top-8'
                : ''
            }`}
          >
            {renderCustomForm(item)}
          </Form.Item>
        );
      })}
    </>
  );
};

export default CustomForm;
